import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import {
  uploadWhatsAppMedia,
  sendWhatsAppMediaMessage,
  getWhatsAppErrorMessage,
  isWhatsAppSessionExpiredResponse,
} from "../services/whatsappApi";
import {
  validateWhatsAppMediaFile,
  shouldConvertAudioToVoice,
  WHATSAPP_CAPTION_MAX,
} from "../utils/whatsappMedia";

export function useWhatsAppMediaSend({
  chatId,
  sessionExpired = false,
  onSessionExpired,
  onSuccess,
} = {}) {
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);

  const sendMedia = useCallback(
    async ({ file, type, caption, convertVoice } = {}) => {
      if (!chatId) {
        toast.error("Select a chat first");
        return { ok: false };
      }

      if (sessionExpired) {
        const msg = "Session expired — use template message instead.";
        toast.error(msg);
        onSessionExpired?.();
        return { ok: false, sessionExpired: true };
      }

      const validation = validateWhatsAppMediaFile(file, type);
      if (!validation.ok) {
        toast.error(validation.error);
        return { ok: false, error: validation.error };
      }

      const trimmedCaption = String(caption || "").trim().slice(0, WHATSAPP_CAPTION_MAX);
      const convert =
        type === "audio" &&
        (convertVoice === true || shouldConvertAudioToVoice(file));

      setUploading(true);
      setProgress(0);

      try {
        const uploadRes = await uploadWhatsAppMedia(file, {
          convert,
          onUploadProgress: setProgress,
        });

        if (!uploadRes?.success || !uploadRes?.url) {
          throw new Error(uploadRes?.message || "Upload failed");
        }

        setUploading(false);
        setSending(true);

        const sendRes = await sendWhatsAppMediaMessage(chatId, type, {
          url: uploadRes.url,
          caption: trimmedCaption || undefined,
          filename: file.name || uploadRes.filename,
        });

        if (sendRes?.success === false || isWhatsAppSessionExpiredResponse(sendRes)) {
          if (isWhatsAppSessionExpiredResponse(sendRes)) {
            const msg =
              sendRes?.message ||
              "Session expired — use template message instead.";
            toast.error(msg);
            onSessionExpired?.();
            return { ok: false, sessionExpired: true };
          }
          throw new Error(sendRes?.message || "Failed to send media");
        }

        const meta = {
          type,
          caption: trimmedCaption,
          filename: file.name || uploadRes.filename,
          upload: uploadRes,
        };
        onSuccess?.(sendRes, meta);
        return { ok: true, data: sendRes, meta };
      } catch (err) {
        const data = err?.response?.data;
        if (isWhatsAppSessionExpiredResponse(data)) {
          const msg =
            data?.message || "Session expired — use template message instead.";
          toast.error(msg);
          onSessionExpired?.();
          return { ok: false, sessionExpired: true };
        }
        const msg = getWhatsAppErrorMessage(err, "Failed to send media");
        toast.error(msg);
        return { ok: false, error: msg };
      } finally {
        setUploading(false);
        setSending(false);
        setProgress(null);
      }
    },
    [chatId, sessionExpired, onSessionExpired, onSuccess],
  );

  return {
    sendMedia,
    uploading,
    sending,
    progress,
    busy: uploading || sending,
  };
}
